import { useState, useEffect } from "react";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Loader2, Fingerprint, Plus, Trash2, Smartphone, AlertCircle, CheckCircle } from "lucide-react";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { toast } from "sonner";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";

// Helper functions for base64url encoding/decoding
function base64URLDecode(str: string): ArrayBuffer {
  const base64 = str.replace(/-/g, '+').replace(/_/g, '/');
  const padding = '='.repeat((4 - base64.length % 4) % 4);
  const binary = atob(base64 + padding);
  const bytes = new Uint8Array(binary.length);
  for (let i = 0; i < binary.length; i++) {
    bytes[i] = binary.charCodeAt(i);
  }
  return bytes.buffer as ArrayBuffer;
}

function arrayBufferToBase64URL(buffer: ArrayBuffer): string {
  const bytes = new Uint8Array(buffer);
  let binary = '';
  for (let i = 0; i < bytes.length; i++) {
    binary += String.fromCharCode(bytes[i]);
  }
  return btoa(binary).replace(/\+/g, '-').replace(/\//g, '_').replace(/=/g, '');
}

interface Passkey {
  id: string;
  friendly_name: string;
  device_type: string;
  created_at: string;
}

export function PasskeyManagement() {
  const [passkeys, setPasskeys] = useState<Passkey[]>([]);
  const [loading, setLoading] = useState(true);
  const [enrolling, setEnrolling] = useState(false);
  const [deleting, setDeleting] = useState<string | null>(null);
  const [error, setError] = useState("");
  const [friendlyName, setFriendlyName] = useState("");
  const [showEnrollForm, setShowEnrollForm] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [passkeyToDelete, setPasskeyToDelete] = useState<Passkey | null>(null);
  const [isSupported, setIsSupported] = useState(true);

  useEffect(() => {
    // Check if WebAuthn is supported
    if (!window.PublicKeyCredential) {
      setIsSupported(false);
    }
    loadPasskeys();
  }, []);

  const loadPasskeys = async () => {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) return;

      const { data, error } = await supabase
        .from("passkeys")
        .select("id, friendly_name, device_type, created_at")
        .eq("user_id", user.id)
        .order("created_at", { ascending: false });

      if (error) throw error;
      setPasskeys(data || []);
    } catch (err) {
      console.error("Error loading passkeys:", err);
    } finally {
      setLoading(false);
    }
  };

  const getDeviceName = (): string => {
    const ua = navigator.userAgent;
    if (/iPhone|iPad|iPod/.test(ua)) return "iPhone/iPad";
    if (/Android/.test(ua)) return "Android-Gerät";
    if (/Mac/.test(ua)) return "Mac";
    if (/Windows/.test(ua)) return "Windows-PC";
    return "Gerät";
  };

  const handleEnrollPasskey = async () => {
    setError("");
    setEnrolling(true);

    try {
      // Step 1: Get registration options from server
      const { data: startData, error: startError } = await supabase.functions.invoke(
        "passkey-register",
        {
          body: { action: "start" },
        }
      );

      if (startError) {
        throw new Error(startError.message);
      }

      if (!startData.options) {
        throw new Error("Keine Registrierungsoptionen erhalten");
      }

      // Step 2: Create credential with browser using native WebAuthn API
      const options = startData.options;
      
      const publicKeyCredentialCreationOptions: PublicKeyCredentialCreationOptions = {
        challenge: base64URLDecode(options.challenge),
        rp: options.rp,
        user: {
          id: base64URLDecode(options.user.id),
          name: options.user.name,
          displayName: options.user.displayName,
        },
        pubKeyCredParams: options.pubKeyCredParams,
        authenticatorSelection: {
          authenticatorAttachment: options.authenticatorSelection?.authenticatorAttachment as AuthenticatorAttachment | undefined,
          residentKey: options.authenticatorSelection?.residentKey as ResidentKeyRequirement | undefined,
          userVerification: options.authenticatorSelection?.userVerification as UserVerificationRequirement | undefined,
        },
        timeout: options.timeout,
        attestation: options.attestation as AttestationConveyancePreference | undefined,
        excludeCredentials: options.excludeCredentials?.map((cred: { id: string; type: "public-key"; transports?: AuthenticatorTransport[] }) => ({
          id: base64URLDecode(cred.id),
          type: cred.type as PublicKeyCredentialType,
          transports: cred.transports as AuthenticatorTransport[],
        })),
      };

      const credential = await navigator.credentials.create({
        publicKey: publicKeyCredentialCreationOptions,
      }) as PublicKeyCredential;

      if (!credential) {
        throw new Error("Keine Anmeldedaten erstellt");
      }

      const response = credential.response as AuthenticatorAttestationResponse;

      // Step 3: Send credential to server
      const { data: finishData, error: finishError } = await supabase.functions.invoke(
        "passkey-register",
        {
          body: {
            action: "finish",
            credential: {
              id: credential.id,
              rawId: arrayBufferToBase64URL(credential.rawId),
              type: credential.type,
              authenticatorAttachment: (credential as PublicKeyCredential & { authenticatorAttachment?: string }).authenticatorAttachment,
              response: {
                clientDataJSON: arrayBufferToBase64URL(response.clientDataJSON),
                attestationObject: arrayBufferToBase64URL(response.attestationObject),
                publicKey: response.getPublicKey ? arrayBufferToBase64URL(response.getPublicKey()!) : null,
                transports: response.getTransports ? response.getTransports() : null,
              },
              clientExtensionResults: credential.getClientExtensionResults(),
            },
            friendlyName: friendlyName || getDeviceName(),
          },
        }
      );

      if (finishError) {
        throw new Error(finishError.message);
      }

      if (!finishData.success) {
        throw new Error(finishData.error || "Registrierung fehlgeschlagen");
      }

      toast.success("Passkey erfolgreich eingerichtet!");
      setShowEnrollForm(false);
      setFriendlyName("");
      await loadPasskeys();

    } catch (err: unknown) {
      console.error("Passkey enrollment error:", err);
      
      if (err instanceof Error) {
        if (err.name === "NotAllowedError") {
          setError("Registrierung abgebrochen oder nicht erlaubt.");
        } else if (err.name === "NotSupportedError") {
          setError("Passkeys werden auf diesem Gerät nicht unterstützt.");
        } else if (err.name === "InvalidStateError") {
          setError("Ein Passkey für dieses Gerät existiert bereits.");
        } else {
          setError(err.message || "Fehler bei der Passkey-Registrierung");
        }
      } else {
        setError("Unbekannter Fehler");
      }
    } finally {
      setEnrolling(false);
    }
  };

  const handleDeletePasskey = async () => {
    if (!passkeyToDelete) return;
    
    setDeleting(passkeyToDelete.id);
    try {
      const { error } = await supabase
        .from("passkeys")
        .delete()
        .eq("id", passkeyToDelete.id);

      if (error) throw error;

      toast.success("Passkey gelöscht");
      await loadPasskeys();
    } catch (err) {
      console.error("Error deleting passkey:", err);
      toast.error("Fehler beim Löschen des Passkeys");
    } finally {
      setDeleting(null);
      setDeleteDialogOpen(false);
      setPasskeyToDelete(null);
    }
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString("de-DE", {
      day: "2-digit",
      month: "2-digit",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  if (!isSupported) {
    return (
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Fingerprint className="h-5 w-5" />
            Passkeys
          </CardTitle>
          <CardDescription>
            Passkeys werden auf diesem Gerät/Browser nicht unterstützt.
          </CardDescription>
        </CardHeader>
      </Card>
    );
  }

  return (
    <>
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Fingerprint className="h-5 w-5" />
            Passkeys verwalten
          </CardTitle>
          <CardDescription>
            Mit Passkeys können Sie sich mit Face ID, Touch ID oder Fingerabdruck anmelden.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          {loading ? (
            <div className="flex items-center justify-center py-4">
              <Loader2 className="h-6 w-6 animate-spin" />
            </div>
          ) : (
            <>
              {/* List of existing passkeys */}
              {passkeys.length > 0 ? (
                <div className="space-y-2">
                  {passkeys.map((passkey) => (
                    <div
                      key={passkey.id}
                      className="flex items-center justify-between p-3 border rounded-lg"
                    >
                      <div className="flex items-center gap-3">
                        <Smartphone className="h-5 w-5 text-muted-foreground" />
                        <div>
                          <p className="font-medium text-sm">{passkey.friendly_name}</p>
                          <p className="text-xs text-muted-foreground">
                            Hinzugefügt am {formatDate(passkey.created_at)}
                          </p>
                        </div>
                      </div>
                      <Button
                        variant="ghost"
                        size="icon"
                        onClick={() => {
                          setPasskeyToDelete(passkey);
                          setDeleteDialogOpen(true);
                        }}
                        disabled={deleting === passkey.id}
                      >
                        {deleting === passkey.id ? (
                          <Loader2 className="h-4 w-4 animate-spin" />
                        ) : (
                          <Trash2 className="h-4 w-4 text-destructive" />
                        )}
                      </Button>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-center py-4 text-muted-foreground">
                  <Fingerprint className="h-8 w-8 mx-auto mb-2 opacity-50" />
                  <p className="text-sm">Noch keine Passkeys eingerichtet</p>
                </div>
              )}

              {/* Add new passkey form */}
              {showEnrollForm ? (
                <div className="space-y-4 pt-4 border-t">
                  {error && (
                    <Alert variant="destructive">
                      <AlertCircle className="h-4 w-4" />
                      <AlertDescription>{error}</AlertDescription>
                    </Alert>
                  )}

                  <div className="space-y-3 bg-muted/50 rounded-lg p-4">
                    <div className="flex items-start gap-3">
                      <CheckCircle className="h-5 w-5 text-primary mt-0.5" />
                      <div>
                        <p className="font-medium text-sm">Schneller anmelden</p>
                        <p className="text-xs text-muted-foreground">Kein Code mehr aus der Authenticator-App</p>
                      </div>
                    </div>
                    <div className="flex items-start gap-3">
                      <Smartphone className="h-5 w-5 text-muted-foreground mt-0.5" />
                      <div>
                        <p className="font-medium text-sm">Erkanntes Gerät</p>
                        <p className="text-xs text-muted-foreground">{getDeviceName()}</p>
                      </div>
                    </div>
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="passkey-name">Name für diesen Passkey</Label>
                    <Input
                      id="passkey-name"
                      type="text"
                      value={friendlyName}
                      onChange={(e) => setFriendlyName(e.target.value)}
                      placeholder={getDeviceName()}
                    />
                  </div>

                  <div className="flex gap-2">
                    <Button
                      onClick={handleEnrollPasskey}
                      disabled={enrolling}
                      className="flex-1"
                    >
                      {enrolling ? (
                        <>
                          <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                          Warte auf Biometrie...
                        </>
                      ) : (
                        <>
                          <Fingerprint className="mr-2 h-4 w-4" />
                          Passkey einrichten
                        </>
                      )}
                    </Button>
                    <Button
                      variant="outline"
                      onClick={() => {
                        setShowEnrollForm(false);
                        setError("");
                        setFriendlyName("");
                      }}
                      disabled={enrolling}
                    >
                      Abbrechen
                    </Button>
                  </div>
                </div>
              ) : (
                <Button
                  variant="outline"
                  onClick={() => setShowEnrollForm(true)}
                  className="w-full"
                >
                  <Plus className="mr-2 h-4 w-4" />
                  Neuen Passkey hinzufügen
                </Button>
              )}
            </>
          )}
        </CardContent>
      </Card>

      {/* Delete confirmation dialog */}
      <AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Passkey löschen?</AlertDialogTitle>
            <AlertDialogDescription>
              Möchten Sie den Passkey "{passkeyToDelete?.friendly_name}" wirklich löschen?
              Sie können sich dann nicht mehr mit diesem Gerät per Passkey anmelden.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Abbrechen</AlertDialogCancel>
            <AlertDialogAction onClick={handleDeletePasskey}>
              Löschen
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}
